import { useMemo } from 'react';
import { Box, DataTable, InlineStack, Stack, type DataTableFeatures } from '@customerio/pluma-components/react';
import type { ColumnDef } from '@tanstack/table-core';
import type { Customer } from '@/services/db/customer/entity';
import { EmailCell } from '@/features/people';
import { EmailSearch } from '@/components/email-search';
import { PeopleFilterBuilder } from '@/pages/workspaces/people/components/people-filter-builder/people-filter-builder';
import { isEmptyString } from '@/utils/string';
import { SearchByControl } from './search-by-control';
import type { PeoplePicker } from './use-people-picker';

type PhoneCellProps = { person: Customer };

function PhoneCell({ person }: PhoneCellProps) {
  const phone = person.attributes.phone ?? '';
  return (
    <Box truncate className="fs-mask">
      {isEmptyString(phone) ? '(No phone)' : phone}
    </Box>
  );
}

function peopleColumns(canIdentifyByPhone: boolean): ColumnDef<DataTableFeatures, Customer>[] {
  const email: ColumnDef<DataTableFeatures, Customer> = {
    id: 'email',
    header: 'email',
    cell: ({ row }) => <EmailCell person={row.original} />,
  };
  const phone: ColumnDef<DataTableFeatures, Customer> = {
    id: 'phone',
    header: 'phone',
    cell: ({ row }) => <PhoneCell person={row.original} />,
  };

  return canIdentifyByPhone ? [email, phone] : [email];
}

const getRowId = (person: Customer) => person.internal_id;

function peopleEmptyState(hasFilters: boolean, hasSearch: boolean, peoplePlural: string) {
  if (hasFilters) {
    return {
      title: hasSearch ? `No ${peoplePlural} matching your search` : `No ${peoplePlural} matching your filters`,
      icon: 'person' as const,
    };
  }

  return {
    title: 'Build your audience',
    description: `Add ${peoplePlural} to see their attributes, events performed, messages received, and more. Use these profiles to personalize your messages.`,
    icon: 'person' as const,
  };
}

type PeopleStepProps = {
  picker: PeoplePicker;
  /** True while any of the wizard's reads or its submit are in flight. */
  isLoading: boolean;
  /** Whether the viewer may create relationships at all. */
  canSelect: boolean;
  /** Phone only gets a column where phone identifies people. */
  canIdentifyByPhone: boolean;
  /** Workspace term for people, plural. */
  peoplePlural: string;
};

export function PeopleStep({ picker, isLoading, canSelect, canIdentifyByPhone, peoplePlural }: PeopleStepProps) {
  const columns = useMemo(() => peopleColumns(canIdentifyByPhone), [canIdentifyByPhone]);
  const rowSelection = useMemo(
    () => Object.fromEntries(picker.selected.map((person) => [getRowId(person), true])),
    [picker.selected],
  );
  const emptyState = peopleEmptyState(picker.hasFilters, !isEmptyString(picker.params.search), peoplePlural);

  return (
    <Box data-test-add-relationships-modal-people-list>
      <Stack gap="200">
        <InlineStack alignBlock="center" gap="150" mt="200">
          <SearchByControl searchLabel="email address" value={picker.searchBy} onChange={picker.activateSearchBy} />
        </InlineStack>
        <Box>
          {picker.searchBy === 'search' ? (
            <EmailSearch term={picker.params.search} onSearch={picker.search} />
          ) : (
            <PeopleFilterBuilder filters={picker.params.filters} onChange={picker.applyFilters} />
          )}
        </Box>
        <DataTable
          caption={`Select ${peoplePlural}`}
          data={[...picker.people]}
          columns={columns}
          getRowId={getRowId}
          withPagination
          pagination={{
            page: picker.params.page,
            pageSize: picker.pageSize,
            totalItemCount: picker.total,
            onPageChange: picker.setPage,
            isManual: true,
          }}
          withRowSelection={canSelect}
          rowSelection={rowSelection}
          onRowSelectionChange={(selection) =>
            picker.setSelected(picker.people.filter((person) => selection[getRowId(person)] === true))
          }
          emptyState={emptyState}
          isLoading={isLoading}
        />
      </Stack>
    </Box>
  );
}
